import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SocketProvider } from './contexts/SocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import useAuth from './hooks/useAuth';

// Layout
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/common/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import StudentsPage from './pages/StudentsPage';
import CreateStudentPage from './pages/CreateStudentPage';
import EditStudentPage from './pages/EditStudentPage';
import StudentDetailPage from './pages/StudentDetailPage';
import BankerPortalPage from './pages/BankerPortalPage';
import BankerFileDetailPage from './pages/BankerFileDetailPage';
import UsersPage from './pages/UsersPage';
import CreateUserPage from './pages/CreateUserPage';
import EditUserPage from './pages/EditUserPage';
import ReportsPage from './pages/ReportsPage';
import CalculatorPage from './pages/CalculatorPage';
import NotificationsPage from './pages/NotificationsPage';
import BankersPage from './pages/BankersPage';
import BankerDetailPage from './pages/BankerDetailPage';
import ConsultancyPage from './pages/ConsultancyPage';
import ConsultancyDetailPage from './pages/ConsultancyDetailPage';
import ConsultancyPortalPage from './pages/ConsultancyPortalPage';
import ConsultancyStudentDetailPage from './pages/ConsultancyStudentDetailPage';
import SettingsPage from './pages/SettingsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import NotFoundPage from './pages/NotFoundPage';

const AppRoutes = () => {
  const { loading, isAuthenticated, role } = useAuth();

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />}
      />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/delete-account" element={<AccountDeletionPage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<MainLayout />}>
          {/* Dashboard */}
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Students - Admin, Manager, Associate */}
          <Route element={<ProtectedRoute requiredRoles={['admin', 'manager', 'associate']} />}>
            <Route path="/students" element={<StudentsPage />} />
            <Route path="/students/new" element={<CreateStudentPage />} />
            <Route path="/students/:id" element={<StudentDetailPage />} />
            <Route path="/students/:id/edit" element={<EditStudentPage />} />
          </Route>

          {/* Banker Portal */}
          <Route element={<ProtectedRoute requiredRoles={['banker']} />}>
            <Route path="/banker-portal" element={<BankerPortalPage />} />
            <Route path="/banker-portal/:id" element={<BankerFileDetailPage />} />
          </Route>

          {/* Consultancy Portal */}
          <Route element={<ProtectedRoute requiredRoles={['consultancy']} />}>
            <Route path="/consultancy-portal" element={<ConsultancyPortalPage />} />
            <Route path="/consultancy-portal/:id" element={<ConsultancyStudentDetailPage />} />
          </Route>

          {/* Bankers Overview - Admin only */}
          <Route element={<ProtectedRoute requiredRoles={['admin']} />}>
            <Route path="/bankers" element={<BankersPage />} />
            <Route path="/bankers/:id" element={<BankerDetailPage />} />
          </Route>

          {/* Consultancy - Admin only */}
          <Route element={<ProtectedRoute requiredRoles={['admin']} />}>
            <Route path="/consultancies" element={<ConsultancyPage />} />
            <Route path="/consultancies/new" element={<ConsultancyPage />} />
            <Route path="/consultancies/:id" element={<ConsultancyDetailPage />} />
          </Route>

          {/* Users - Admin only */}
          <Route element={<ProtectedRoute requiredRoles={['admin']} />}>
            <Route path="/users" element={<UsersPage />} />
            <Route path="/users/new" element={<CreateUserPage />} />
            <Route path="/users/:id/edit" element={<EditUserPage />} />
          </Route>

          {/* Reports - Admin, Manager */}
          <Route element={<ProtectedRoute requiredRoles={['admin', 'manager']} />}>
            <Route path="/reports" element={<ReportsPage />} />
          </Route>

          {/* Settings - Admin */}
          <Route element={<ProtectedRoute requiredRoles={['admin']} />}>
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Calculator - All roles */}
          <Route path="/calculator" element={<CalculatorPage />} />

          {/* Notifications - All roles */}
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>
      </Route>

      {/* Default redirect */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            role === 'banker' ? (
              <Navigate to="/banker-portal" />
            ) : role === 'consultancy' ? (
              <Navigate to="/consultancy-portal" />
            ) : (
              <Navigate to="/dashboard" />
            )
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SocketProvider>
          <NotificationProvider>
            <AppRoutes />
          </NotificationProvider>
        </SocketProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
